import { Formik, FormikProps } from 'formik';
import React, { forwardRef, FocusEvent, ChangeEvent } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { FormFieldType } from '../../../enums';
import { AddressFormData } from '../../../interfaces';

import { useSmallLocationFormValidation } from './smallLocationForm.hooks';
import { FieldsRow, FormikForm, HiddenInput, StyledFormField } from './smallLocationForm.styled';
import { SmallLocationFormProps } from './smallLocationForm.types';

export const SmallLocationForm: React.FC<SmallLocationFormProps> = forwardRef((
  props: SmallLocationFormProps, ref: React.Ref<FormikProps<AddressFormData>>
): JSX.Element => {
  const { initialValues, isFormSubmitted, className }: SmallLocationFormProps = props;
  const smallLocationFormValidationSchema: Yup.SchemaOf<AddressFormData> = useSmallLocationFormValidation();
  const { t }: TransProps<never> = useTranslation();

  return (
    <Formik
      initialValues={{ ...initialValues, country: 'Polska' }}
      onSubmit={(): void => undefined}
      innerRef={ref}
      validationSchema={smallLocationFormValidationSchema}
      validateOnChange={isFormSubmitted}
      validateOnBlur={isFormSubmitted}
      className={className}
    >
      {({ handleSubmit, setFieldValue, errors, values }: FormikProps<AddressFormData>): JSX.Element => (
        <FormikForm onSubmit={handleSubmit}>
          <FieldsRow>
            <StyledFormField
              label={t('onarte.ui.smallLocationForm.street.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.street}
              fieldName='street'
              validationMessage={isFormSubmitted ? errors.street : undefined}
            />
          </FieldsRow>
          <FieldsRow>
            <StyledFormField
              label={t('onarte.ui.smallLocationForm.houseNumber.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.houseNumber}
              fieldName='houseNumber'
              validationMessage={isFormSubmitted ? errors.houseNumber : undefined}
            />
            <StyledFormField
              label={t('onarte.ui.smallLocationForm.apartmentNumber.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.apartmentNumber ?? ''}
              fieldName='apartmentNumber'
              validationMessage={isFormSubmitted ? errors.apartmentNumber : undefined}
            />
          </FieldsRow>
          <FieldsRow>
            <StyledFormField
              label={t('onarte.ui.smallLocationForm.postalCode.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.postalCode}
              fieldName='postalCode'
              validationMessage={isFormSubmitted ? errors.postalCode : undefined}
              additionalFieldProps={{
                format: '##-###'
              }}
            />
            <StyledFormField
              label={t('onarte.ui.smallLocationForm.city.label')}
              fieldType={FormFieldType.Input}
              setFieldValue={setFieldValue}
              value={values.city}
              fieldName='city'
              validationMessage={isFormSubmitted ? errors.city : undefined}
            />
            <HiddenInput
              type='hidden'
              value={values.country}
              name='country'
              onChange={(e: ChangeEvent<HTMLInputElement>): void => setFieldValue('country', e.target.value)}
              onBlur={(e: FocusEvent<HTMLInputElement>): void => setFieldValue('country', e.target.value)}
            />
          </FieldsRow>
        </FormikForm>
      )}
    </Formik>
  );
});
